import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  styled,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Tooltip,
  Avatar,
  Card,
  useMediaQuery,
} from '@mui/material';
import {
  EmojiEmotions,
  Send,
  AttachFile,
  Close,
  AddLink,
  FormatAlignLeft,
  EditNote,
  Attachment,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useAddFeedbackMutation, useEditFeedbackMutation } from '../feedbackApiSlice';
import toast from 'react-hot-toast';
import EmojiPicker from 'emoji-picker-react';
import 'react-quill/dist/quill.snow.css';
import { priorityIcon } from '../utility/utilities';
import ReactQuill from 'react-quill';
import { quillFix } from '../../../utils/quillFix';
import AttachAvatar from '../../../common/AttachAvatar';
import { useTheme } from '@emotion/react';
import { variants } from '../../../variants/variants';
import { set } from 'date-fns';

const StyledPaper = styled(Paper)({
  padding: '1rem 0.5rem 0.5rem 0.5rem',
  display: 'flex',
  alignItems: 'flex-end',
  background: 'transparent',
  flexDirection: 'column',
  minHeight: '60px',
  position: 'absolute',
  boxShadow: 'none',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 100,
});

const QuillWrapper = styled(Box)(({ theme }) => ({
  border: '1px solid lightgrey',
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  alignContent: 'center',
  width: '73%',
  position: 'relative',
  paddingLeft: '30px',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '15px !important',
  },
  background: 'white',
  [theme.breakpoints.down('lg')]: {
    borderTopLeftRadius: 0,
    width: '100%',
  },
}));

const StyledQuill = styled(ReactQuill)({
  '& .ql-container': {
    border: 'none',
    border: 0,
    backgroundColor: '#ffffff',
    '&.ql-focused': {
      boxShadow: '#3f51b5 0 0 0 2px',
    },
  },
  '& .ql-container.ql-snow': {
    height: 'auto',
    border: 0,
  },
  '& .ql-editor': {
    maxHeight: '150px',
    minHeight: '40px',
    overflowY: 'scroll',
    border: 0,
  },
  '& .ql-toolbar': {
    display: 'none',
  },
  '.ql-editor.ql-blank::before': {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  '&.show-toolbar .ql-toolbar': {
    display: 'block',
    border: 'none',
    background: 'white',
  },
  '.ql-tooltip': {
    bottom: 'auto !important',
    top: '-47px !important' /* Adjust the value as needed */,
    left: '0% !important',
    boxShadow: 'none',
    borderRadius: '8px',
    marginTop: '0% !important',
  },
  '.ql-tooltip > input': {
    padding: '5px 10px',
    maxWidth: 120,
    borderRadius: '8px',
    outlineColor: 'lightgray',
    outlineWidth: '0.2px',
    cursor: 'default',
    '&:hover': {
      backgroundColor: '#f1f1f1',
    },
  },
});

const validationSchema = Yup.object({
  feedback: Yup.string()
    .trim()
    .matches(/.*\S.*/, 'Feedback must contain at least one non-whitespace character.')
    .required('Feedback is required'),
  priority: Yup.string().required('Priority is required'),
  category: Yup.string().required('Category is required'),
  attachments: Yup.array().of(Yup.mixed()).max(4, 'You can upload up to 4 attachments.').notRequired(),
});

const FeedbackForm = ({
  editingFeedback,
  setEditingFeedback,
  showScrollButton,
  onChange,
  inputRef,
  showToolbar,
  setShowToolbar,
}) => {
  const [addFeedback] = useAddFeedbackMutation();
  const [editFeedback] = useEditFeedbackMutation();
  const [filePreviews, setFilePreviews] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const quillRef = useRef(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Detect if the screen is mobile
  const [sendDisabled, setSendDisabled] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      feedback: '',
      priority: 'low',
      category: 'feature request',
      attachments: [],
    },
    validationSchema,
    onSubmit: async (values, { setFieldValue }) => {
      quillRef.current.getEditor().focus(); // Focus the Quill editor after submission

      const { feedback, priority, category, attachments } = values;

      const slicedText = quillFix(feedback);

      if (slicedText) {
        const formData = new FormData();
        formData.append('feedbackText', slicedText);
        formData.append('priority', priority);
        formData.append('category', category);
        if (attachments) {
          attachments.forEach((file) => {
            formData.append('attachments', file);
          });
        }
        const action = editingFeedback
          ? editFeedback({
              id: editingFeedback.feedbackId,
              data: { feedbackText: slicedText, priority, category },
            })
          : addFeedback(formData);

        try {
          setSendDisabled(true);
          await toast.promise(action.unwrap(), {
            loading: 'Processing feedback...',
            success: editingFeedback ? 'Feedback edited successfully!' : 'Feedback submitted successfully!',
            error: (err) => err.data?.message || 'Error processing feedback. Please try again.',
          });
          setEditingFeedback(null);
          setFieldValue('feedback', '');
          setFieldValue('attachments', []);
          setFilePreviews([]);
          quillRef.current.getEditor().focus(); // Focus the Quill editor after submission
          setSendDisabled(false);
        } catch (error) {
          console.error('Error processing feedback:', error);
        }
      }
    },
  });

  useEffect(() => {
    if (editingFeedback) {
      formik.setValues({
        feedback: editingFeedback.feedbackText,
        priority: editingFeedback.priority,
        category: editingFeedback.category,
      });
      if (quillRef.current) quillRef.current.getEditor().focus();
    }
  }, [editingFeedback]);

  const handleEmojiPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleEmojiClick = (emojiObject) => {
    const quillText = formik.values.feedback;
    const trimmedFeedback =
      quillText.startsWith('<p><br></p>') || !quillText.includes('</p><p><br></p>')
        ? quillText.replaceAll('<p><br></p>', '').trim()
        : quillText.replaceAll('<p><br></p>', '<br>').trim();

    const formattedText = trimmedFeedback.endsWith('</p>')
      ? trimmedFeedback.slice(0, trimmedFeedback.length - 4) + emojiObject.emoji + '</p>'
      : trimmedFeedback + emojiObject.emoji;
    formik.setFieldValue('feedback', formattedText);
    setAnchorEl(null);
  };

  const handleQuillChange = (content) => {
    formik.setFieldValue('feedback', content);
    onChange(inputRef.current.clientHeight);
  };
  const handleCancelEditing = () => {
    setEditingFeedback(null);
    formik.resetForm();
    setFilePreviews([]);
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files);
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'video/mp4'];
    const validFiles = files.filter((file) => {
      return allowedTypes.includes(file.type);
    });
    const newAttachments = [...formik.values.attachments, ...validFiles].slice(0, 4);
    formik.setFieldValue('attachments', newAttachments);

    const previews = newAttachments.map((file) => {
      const fileType = file.type.split('/')[0];
      if (fileType === 'video') {
        return {
          type: 'video',
          url: URL.createObjectURL(file),
        };
      } else {
        return {
          type: 'image',
          url: URL.createObjectURL(file),
        };
      }
    });
    setFilePreviews(previews);
  };

  const handleRemoveAttachment = (index) => {
    const newAttachments = formik.values.attachments.filter((_, i) => i !== index);
    formik.setFieldValue('attachments', newAttachments);
    const newPreviews = filePreviews.filter((_, i) => i !== index);
    setFilePreviews(newPreviews);
  };
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        formik.handleSubmit();
      }
    },
    [formik]
  );
  // useEffect(() => {
  //   const quillEditor = quillRef.current.getEditor();
  //   const quillEditorInput = quillEditor.root;

  //   quillEditorInput.addEventListener('keydown', handleKeyDown);

  //   return () => {
  //     quillEditorInput.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, [handleKeyDown]);
  return (
    <StyledPaper ref={inputRef}>
      <form
        onSubmit={formik.handleSubmit}
        style={{ width: '100%', display: 'flex', alignItems: 'flex-end', position: 'relative' }}
      >
        <Box
          sx={{
            display: 'flex',
            [theme.breakpoints.down('lg')]: {
              position: 'absolute',
              left: 0,
              bottom: `${inputRef?.current?.clientHeight - 40}px`,
              zIndex: -1,
            },
          }}
        >
          <FormControl sx={{ minWidth: 120, borderRadius: '8px' }}>
            <InputLabel id='category-label' sx={{ [theme.breakpoints.down('lg')]: { display: 'none' } }}>
              Category
            </InputLabel>
            <Select
              labelId='category-label'
              id='category'
              name='category'
              value={formik.values.category}
              onChange={(value) => {
                if (quillRef.current) quillRef.current.getEditor().focus();
                formik.handleChange(value);
              }}
              {...(theme.breakpoints.down('lg') ? { label: 'Category' } : {})}
              sx={{
                borderRadius: '20px',
                background: 'white',
                height: '45px',
                fontSize: variants.BODY,
                textOverflow: 'ellipsis',
                [theme.breakpoints.down('lg')]: {
                  '& .MuiSelect-icon': {
                    top: '40%', // Vertically center the arrow
                    transform: 'translateY(-40%)', // Adjust to exactly center it
                  },
                  '> fieldset': { border: 0, display: 'none' },
                  'div#category': {
                    border: 1,
                    pt: 0,
                    pb: 1,
                    borderRadius: '20px',
                    borderTopRightRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    borderColor: 'divider',
                  },
                  'label#category-label': {
                    display: 'none',
                  },
                },
              }}
            >
              <MenuItem value='feature request' sx={{ fontSize: variants.BODY, textOverflow: 'ellipsis' }}>
                Feature Request
              </MenuItem>
              <MenuItem value='bug report' sx={{ fontSize: variants.BODY }}>
                Bug Report
              </MenuItem>
              <MenuItem value='other' sx={{ fontSize: variants.BODY }}>
                Other
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120, borderRadius: '8px', width: '9vw' }}>
            <InputLabel id='priority-label' sx={{ [theme.breakpoints.down('lg')]: { display: 'none' } }}>
              Priority
            </InputLabel>
            <Select
              labelId='priority-label'
              id='priority'
              name='priority'
              value={formik.values.priority}
              onChange={(value) => {
                formik.handleChange(value);
                if (quillRef.current) quillRef.current.getEditor().focus();
              }}
              {...(theme.breakpoints.down('lg') ? { label: 'Priority' } : {})}
              sx={{
                borderRadius: '20px',
                background: 'white',
                alignItems: 'center',
                height: '45px',
                fontSize: variants.BODY,
                display: 'flex',
                alignItems: 'center', // Align content vertically
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center', // Align selected item content
                },
                [theme.breakpoints.down('lg')]: {
                  '& .MuiSelect-icon': {
                    top: '40%', // Vertically center the arrow
                    transform: 'translateY(-40%)', // Adjust to exactly center it
                  },
                  '> fieldset': { border: 0, display: 'none' },
                  'div#priority': {
                    border: 1,
                    pt: 0,
                    pb: 1,
                    borderRadius: '20px',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    borderColor: 'divider',
                  },
                  'label#priority-label': {
                    display: 'none',
                  },
                },
              }}
            >
              <MenuItem value='urgent' sx={{ fontSize: variants.BODY }}>
                {' '}
                {priorityIcon('urgent')}Urgent
              </MenuItem>
              <MenuItem value='high' sx={{ fontSize: variants.BODY }}>
                {' '}
                {priorityIcon('high')}High
              </MenuItem>
              <MenuItem value='medium' sx={{ fontSize: variants.BODY }}>
                {priorityIcon('medium')}Medium
              </MenuItem>
              <MenuItem value='low' sx={{ fontSize: variants.BODY }}>
                {priorityIcon('low')}Low
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        <QuillWrapper>
          <StyledQuill
            // onKeyDown={handleKeyDown}
            ref={quillRef}
            theme='snow'
            placeholder='Type a message...'
            value={formik.values.feedback}
            onChange={(content) => handleQuillChange(content)}
            modules={{
              toolbar: [
                ['bold', 'italic', 'strike', 'blockquote'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link'],
              ],
            }}
            formats={['bold', 'italic', 'strike', 'blockquote', 'list', 'bullet', 'link']}
            className={showToolbar ? 'show-toolbar' : ''}
            sx={{
              width: 'calc(100% - 80px)',
              marginBottom: 0,
              border: 'none',
              '& .ql-container': {
                border: 'none',
              },
            }}
          />
          {!isMobile && (
            <InputAdornment
              position='end'
              sx={{ position: 'absolute', left: '-7px', bottom: '22px', display: 'flex', gap: '10px' }}
            >
              <IconButton onClick={handleEmojiPopover} edge='end'>
                <EmojiEmotions />
              </IconButton>
            </InputAdornment>
          )}
          <InputAdornment
            position='end'
            sx={{ position: 'absolute', right: '15px', bottom: '22px', display: 'flex', gap: '10px' }}
          >
            {true ? null : (
              <>
                <input type='file' multiple hidden id='attachment-input' onChange={handleAttachmentChange} />
                <label htmlFor='attachment-input'>
                  <Tooltip title='Attachments'>
                    <IconButton component='span' sx={{ cursor: 'default', marginRight: '-5px' }}>
                      <Attachment />
                    </IconButton>
                  </Tooltip>
                </label>
              </>
            )}
            <IconButton
              onClick={() => {
                setShowToolbar(!showToolbar);
                if (quillRef.current) quillRef.current.getEditor().focus();
              }}
              edge='end'
            >
              {showToolbar ? <KeyboardArrowDown /> : <EditNote />}
            </IconButton>
            <IconButton type='submit' edge='end' disabled={sendDisabled}>
              <Send />
            </IconButton>
          </InputAdornment>
          {editingFeedback ? (
            <InputAdornment
              position='end'
              sx={{
                position: 'absolute',
                left: '-3px',
                top: '20px',
                display: 'flex',
                gap: '10px',
              }}
            >
              <IconButton
                onClick={handleCancelEditing}
                edge='end'
                sx={{ background: 'lightgrey', borderRadius: '50%' }}
              >
                <Close sx={{ fontSize: '12px', m: '-5px', color: 'black' }} />
              </IconButton>
            </InputAdornment>
          ) : null}
        </QuillWrapper>
        <Card
          sx={{
            background: formik.values?.attachments?.length > 0 ? 'white' : 'none',
            boxShadow: formik.values?.attachments?.length > 0 ? null : 'none',
            borderRadius: '20px',
            display: 'flex',
            justifyContent: 'center',
            padding: '10px',
            gap: 3,
            zIndex: 101,
            position: 'absolute',
            top: isMobile ? '-95px' : '-80px',
            left: isMobile ? 0 : '20%',
            // transform: 'translateX(-50%)',
          }}
        >
          <Box sx={{}}>
            {formik.values.attachments && formik.values.attachments.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-end',
                  gap: 1,
                  flexWrap: 'nowrap',
                  cursor: 'default',
                }}
              >
                {filePreviews.map((preview, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mr: '10px', position: 'relative' }}>
                    <Tooltip title={formik.values.attachments[index].name} arrow>
                      <AttachAvatar
                        src={preview.url}
                        isVideoPreview={preview.type == 'video'}
                        publicCall={false}
                        variant='square'
                        sx={{ width: 50, height: 50 }}
                      />
                    </Tooltip>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        right: '-15px',
                        top: '0%',
                        transform: 'translateY(-50%)',
                        background: 'lightgrey',
                        borderRadius: '50%',
                        cursor: 'default',
                      }}
                      onClick={() => handleRemoveAttachment(index)}
                    >
                      <Close sx={{ fontSize: '12px', m: '-5px', color: 'black' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Card>
        <Popover
          id='emoji-picker-popover'
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <EmojiPicker onEmojiClick={handleEmojiClick} />
        </Popover>
      </form>
    </StyledPaper>
  );
};

export default FeedbackForm;
